// Extended mock data for full page views. The Overview-level summary lives in
// useDashboard.js; these arrays power the dedicated /transactions, /compliance,
// and /agent-reports pages until the real backend is wired.

const NOW = Date.now();
const HOUR = 3600 * 1000;
const DAY = 24 * HOUR;

const CORRIDORS = ['CAD_KES', 'CAD_NGN', 'CAD_GHS', 'CAD_ZAR', 'CAD_UGX', 'CAD_TZS'];
const NETWORKS = ['base', 'polygon', 'solana'];
const STATUSES = ['confirmed', 'confirmed', 'confirmed', 'confirmed', 'pending', 'failed'];
const DIRECTIONS = ['onramp', 'offramp'];

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
function randAddr() {
  const hex = '0123456789abcdef';
  let s = '0x';
  for (let i = 0; i < 8; i++) s += hex[Math.floor(Math.random() * 16)];
  return s + '...';
}
function randTxHash() {
  const hex = '0123456789abcdef';
  let s = '0x';
  for (let i = 0; i < 12; i++) s += hex[Math.floor(Math.random() * 16)];
  return s;
}

export const recentTransactions = Array.from({ length: 28 }, (_, i) => {
  const status = pick(STATUSES);
  const direction = pick(DIRECTIONS);
  const corridor = pick(CORRIDORS);
  const network = pick(NETWORKS);
  const minutesAgo = i * 18 + Math.floor(Math.random() * 12);
  return {
    id: `tx-${i + 1}`,
    hash: randTxHash(),
    direction,
    corridor,
    network,
    status,
    amount_cad: Math.round((Math.random() * 4500 + 80) * 100) / 100,
    amount_usdc: Math.round((Math.random() * 3300 + 60) * 100) / 100,
    fee_cad: Math.round(Math.random() * 12 * 100) / 100,
    gas_cost_usd: Math.round(Math.random() * 0.05 * 10000) / 10000,
    latency_ms: Math.floor(Math.random() * 2200) + 320,
    sender: randAddr(),
    recipient: randAddr(),
    created_at: new Date(NOW - minutesAgo * 60 * 1000).toISOString(),
  };
});

const FLAG_TYPES = ['large_transaction', 'structuring', 'velocity', 'sanctions_match', 'unusual_pattern'];
const FLAG_TITLES = {
  large_transaction: (amt) => `Large transaction: $${amt.toLocaleString()} CAD`,
  structuring: (n) => `Possible structuring: ${n} transactions near threshold`,
  velocity: (n) => `Velocity alert: ${n} transactions in 60min`,
  sanctions_match: () => 'Possible sanctions list match — manual review required',
  unusual_pattern: () => 'Unusual transaction pattern detected by AML model',
};

export const allFlags = Array.from({ length: 14 }, (_, i) => {
  const flag_type = pick(FLAG_TYPES);
  const severity = i < 2 ? 'critical' : i < 6 ? 'high' : i < 10 ? 'medium' : 'low';
  const status = pick(['open', 'open', 'reviewing', 'escalated', 'dismissed', 'str_filed']);
  const corridor = Math.random() > 0.2 ? pick(CORRIDORS) : null;
  const param = flag_type === 'large_transaction'
    ? Math.round(Math.random() * 8000 + 1500)
    : Math.floor(Math.random() * 6) + 3;
  return {
    id: `flag-${i + 1}`,
    flag_type,
    severity,
    status,
    title: FLAG_TITLES[flag_type](param),
    corridor,
    sender: randAddr(),
    detected_by: i % 3 === 0 ? 'rule-engine' : 'claude-agent',
    created_at: new Date(NOW - (i * 4 + Math.random() * 3) * HOUR).toISOString(),
  };
});

const REPORT_BODIES = {
  hourly: [
    '23 transactions processed. $14,200 CAD total volume. 0 new flags. RIE routed 91% via Base. All corridors operational. Paymaster balance healthy at 1.23 ETH.',
    '18 transactions processed. $11,800 CAD total. 1 velocity flag generated. RIE routed 85% via Base, 15% Polygon fallback due to gas spike.',
    '12 transactions processed. $8,400 CAD. No anomalies detected. Average latency 950ms. Paymaster nominal.',
    '31 transactions processed. $19,750 CAD. 1 large transaction flag (sender 0xa3f...). Routing 88% Base, 12% Solana.',
    '9 transactions processed. $6,200 CAD. Quiet hour. No flags. All routes nominal.',
    '27 transactions processed. $17,400 CAD. 2 velocity flags merged into single alert. Routing 92% Base.',
  ],
  eod: [
    'EOD Report: 147 transactions, $98,420 CAD total. Corridors: KES 39%, NGN 32%, GHS 18%, ZAR 11%. RIE efficiency: 95.2% (7 fallbacks). 4 compliance flags. Treasury: $45,230 USDC.',
    'EOD Report: 162 transactions, $112,300 CAD. Corridors: KES 41%, NGN 28%, GHS 21%, ZAR 10%. RIE 96.8%. 2 compliance flags. Recommendation: review structuring on 0xb7c... — 4 tx at $950–$990 in 6h.',
    'EOD Report: 134 transactions, $89,100 CAD. RIE efficiency: 93.5%. 6 flags (3 dismissed, 2 reviewing, 1 STR-filed). Paymaster topped up overnight. No customer-facing incidents.',
  ],
};

export const extendedReports = Array.from({ length: 18 }, (_, i) => {
  const isEOD = i % 6 === 5;
  const report_type = isEOD ? 'eod' : 'hourly';
  const bodies = REPORT_BODIES[report_type];
  const hoursAgo = isEOD ? Math.floor(i / 6) * 24 + 8 : i * 1 + Math.floor(Math.random() * 2);
  return {
    id: `report-${i + 1}`,
    report_type,
    summary_text: bodies[i % bodies.length],
    flags_referenced: report_type === 'eod' ? Math.floor(Math.random() * 4) + 1 : Math.floor(Math.random() * 2),
    transactions_reviewed: report_type === 'eod' ? Math.floor(Math.random() * 80) + 100 : Math.floor(Math.random() * 30) + 8,
    created_at: new Date(NOW - hoursAgo * HOUR).toISOString(),
  };
});

export const corridorMetadata = {
  CAD_KES: { name: 'Kenya', flag: '🇰🇪', currency: 'KES' },
  CAD_NGN: { name: 'Nigeria', flag: '🇳🇬', currency: 'NGN' },
  CAD_GHS: { name: 'Ghana', flag: '🇬🇭', currency: 'GHS' },
  CAD_ZAR: { name: 'South Africa', flag: '🇿🇦', currency: 'ZAR' },
  CAD_UGX: { name: 'Uganda', flag: '🇺🇬', currency: 'UGX' },
  CAD_TZS: { name: 'Tanzania', flag: '🇹🇿', currency: 'TZS' },
};

export const networkMetadata = {
  base: { name: 'Base', short: 'BASE', chain_id: 8453 },
  polygon: { name: 'Polygon', short: 'POLY', chain_id: 137 },
  solana: { name: 'Solana', short: 'SOL', chain_id: null },
};

export const formatCurrency = (val, opts = {}) => {
  const { currency = 'CAD', compact = true } = opts;
  const n = Number(val) || 0;
  if (compact) {
    if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
    return `$${n.toFixed(2)}`;
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n);
};

export const timeAgo = (input) => {
  if (input == null) return '—';
  const t = input instanceof Date ? input.getTime() : new Date(input).getTime();
  if (Number.isNaN(t)) return '—';
  const diff = Date.now() - t;
  if (diff < 60_000) return 'just now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};
